import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowLeft, Chrome } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/";

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    try {
      const res = await axios.get(`${API}/auth/google/login?user_type=user`);
      // Redirect to Google OAuth
      window.location.href = res.data.auth_url;
    } catch (error) {
      toast.error("Error al iniciar con Google");
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      
      // Clear any stale tokens/cache first
      localStorage.removeItem("token");
      
      // Update auth state
      login(res.data.token, res.data.user);
      toast.success("¡BIENVENIDO DE VUELTA!");
      
      // Small delay to ensure React state propagates before navigation
      // This fixes the race condition where Admin.jsx reads stale token
      setTimeout(() => {
        if (res.data.user.user_type === "admin") {
          navigate("/admin", { replace: true });
        } else if (res.data.user.user_type === "bar") {
          navigate("/dashboard", { replace: true });
        } else {
          navigate(from, { replace: true });
        }
      }, 50);
    } catch (error) {
      toast.error(error.response?.data?.detail || "ERROR AL INICIAR SESIÓN");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 font-mono text-sm text-zinc-500 hover:text-primary transition-colors mb-8"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4" />
          VOLVER
        </Link>

        <div className="bg-[#111] border border-zinc-800 p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-black tracking-tighter">
              <span className="text-white">ROCK</span><span className="text-primary">MAPS</span>
            </h1>
            <p className="font-mono text-sm text-zinc-500 mt-2">ACCEDE A TU CUENTA</p>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="font-mono text-xs text-zinc-500 mb-2 block">
                EMAIL
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="input-ruido w-full"
                placeholder="tu@email.com"
                data-testid="login-email"
              />
            </div>

            <div>
              <Label htmlFor="password" className="font-mono text-xs text-zinc-500 mb-2 block">
                CONTRASEÑA
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="input-ruido w-full pr-10"
                  placeholder="••••••••"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-ruido bg-primary text-white font-mono py-6 border-2 border-primary"
              data-testid="login-submit"
            >
              {loading ? "ENTRANDO..." : "ENTRAR"}
            </Button>
            
            <div className="text-right">
              <Link to="/reset-password" className="text-zinc-500 hover:text-primary font-mono text-xs">
                ¿Olvidaste tu contraseña?
              </Link>
            </div>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-zinc-800"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-[#111] px-4 text-zinc-600 font-mono">O CONTINÚA CON</span>
            </div>
          </div>

          {/* Google Login Button */}
          <Button
            type="button"
            onClick={handleGoogleLogin}
            disabled={googleLoading}
            className="w-full bg-zinc-900 hover:bg-zinc-800 text-white font-mono py-6 border border-zinc-700 flex items-center justify-center gap-3"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {googleLoading ? "CONECTANDO..." : "ENTRAR CON GOOGLE"}
          </Button>

          {/* Register Link */}
          <div className="mt-6 text-center">
            <p className="text-zinc-500 text-sm">
              ¿No tienes cuenta?{" "}
              <Link to="/register" className="text-primary font-mono hover:underline" data-testid="register-link">
                REGÍSTRATE
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
